import { useState, useEffect, useMemo, useRef } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  FREQUENCY_OPTIONS,
  STATUS_OPTIONS,
  toDateStr,
  parseDateStr,
  fmt12,
  describeRecurrence,
  resolveRange,
  effectiveStatus,
  statusLabel,
  statusColor,
  noteToHtml,
  NOTE_TEMPLATE_HTML,
} from './coachingCallsHelpers';

const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (ET)' },
  { value: 'America/Chicago',     label: 'Central (CT)' },
  { value: 'America/Denver',      label: 'Mountain (MT)' },
  { value: 'America/Phoenix',     label: 'Mountain – AZ (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage',   label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HT)' },
  { value: 'America/Toronto',     label: 'Toronto (ET)' },
  { value: 'America/Vancouver',   label: 'Vancouver (PT)' },
  { value: 'Europe/London',       label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',        label: 'Central Europe (CET)' },
  { value: 'Europe/Berlin',       label: 'Berlin (CET)' },
  { value: 'Australia/Sydney',    label: 'Sydney (AEDT)' },
  { value: 'Asia/Tokyo',          label: 'Tokyo (JST)' },
];

const DOW_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const DURATION_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
  { value: 90, label: '90 min' },
];

export function CoachingCalls() {
  const { profile } = useAuth();
  const now = new Date();

  // ── Data
  const [athletes, setAthletes] = useState([]);
  const [series, setSeries] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [notesByKey, setNotesByKey] = useState({}); // 'athleteId:date' → noteRow
  const [loading, setLoading] = useState(true);

  // ── Calendar nav
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calYear, setCalYear] = useState(now.getFullYear());

  // ── Filters (calendar-first surface)
  const [filterAthlete, setFilterAthlete] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // ── Modals
  const [createModal, setCreateModal] = useState(null); // { date }
  const [detailModal, setDetailModal] = useState(null); // occurrence
  const [editChoice, setEditChoice] = useState(null);   // { occurrence, mode: 'edit'|'delete' }

  // ── Drag/drop (kept for non-recurring + as a quick "move this one"
  //    affordance on existing recurring instances — opens a confirm).
  const [draggedCall, setDraggedCall] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [moveModal, setMoveModal] = useState(null);

  // ── Active Schedules accordion
  const [showActiveSchedules, setShowActiveSchedules] = useState(false);

  // ── Load
  useEffect(() => {
    if (profile) {
      loadAthletes();
      loadSeries();
    }
  }, [profile]);

  useEffect(() => {
    if (series.length) {
      loadOverrides();
      loadMonthNotes();
    } else {
      setOverrides([]);
      setNotesByKey({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, calMonth, calYear]);

  async function loadAthletes() {
    const { data: aps } = await supabase
      .from('athlete_profiles')
      .select('id')
      .eq('coach_id', profile.id);
    if (!aps?.length) { setAthletes([]); return; }
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', aps.map(a => a.id))
      .order('full_name');
    setAthletes(data ?? []);
  }

  async function loadSeries() {
    setLoading(true);
    const { data } = await supabase
      .from('coaching_call_schedules')
      .select('*')
      .eq('coach_id', profile.id)
      .eq('active', true)
      .order('day_of_week');
    setSeries(data ?? []);
    setLoading(false);
  }

  async function loadOverrides() {
    const ids = series.map(s => s.id);
    if (!ids.length) return;
    const { data } = await supabase
      .from('coaching_call_overrides')
      .select('*')
      .eq('coach_id', profile.id)
      .in('schedule_id', ids);
    setOverrides(data ?? []);
  }

  async function loadMonthNotes() {
    const monthStr = String(calMonth + 1).padStart(2, '0');
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const start = `${calYear}-${monthStr}-01`;
    const end   = `${calYear}-${monthStr}-${String(daysInMonth).padStart(2, '0')}`;
    const athleteIds = [...new Set(series.map(s => s.athlete_id))];
    if (!athleteIds.length) { setNotesByKey({}); return; }
    const { data } = await supabase
      .from('coaching_call_notes')
      .select('id, athlete_id, schedule_id, call_date, notes, updated_at')
      .eq('coach_id', profile.id)
      .in('athlete_id', athleteIds)
      .gte('call_date', start)
      .lte('call_date', end);
    const map = {};
    (data ?? []).forEach(n => { map[`${n.athlete_id}:${n.call_date}`] = n; });
    setNotesByKey(map);
  }

  // ── Resolve all occurrences in the current month, then apply
  //    the athlete/status filters. The calendar reads from
  //    `filteredOccurrences`.
  const todayStr = toDateStr(now);
  const monthStartStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`;
  const monthEndStr = (() => {
    const days = new Date(calYear, calMonth + 1, 0).getDate();
    return `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(days).padStart(2, '0')}`;
  })();

  const occurrences = useMemo(
    () => resolveRange(series, overrides, monthStartStr, monthEndStr),
    [series, overrides, monthStartStr, monthEndStr]
  );

  const filteredOccurrences = useMemo(() => {
    return occurrences.filter(o => {
      if (filterAthlete !== 'all' && o.athleteId !== filterAthlete) return false;
      if (filterStatus !== 'all') {
        const eff = effectiveStatus(o, todayStr);
        if (filterStatus === 'upcoming' && eff !== 'upcoming' && eff !== 'today') return false;
        if (filterStatus !== 'upcoming' && eff !== filterStatus) return false;
      }
      return true;
    });
  }, [occurrences, filterAthlete, filterStatus, todayStr]);

  // Group by date for the calendar.
  const occByDate = useMemo(() => {
    const m = {};
    for (const o of filteredOccurrences) {
      if (!m[o.date]) m[o.date] = [];
      m[o.date].push(o);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''));
    }
    return m;
  }, [filteredOccurrences]);

  function getAthleteName(id) {
    return athletes.find(a => a.id === id)?.full_name ?? 'Athlete';
  }

  // ── Calendar grid
  const weeks = (() => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    const ms = String(calMonth + 1).padStart(2, '0');
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${calYear}-${ms}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  })();

  function goPrev() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  }
  function goNext() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  }
  function goToday() {
    setCalMonth(now.getMonth());
    setCalYear(now.getFullYear());
  }

  // ── Drag & drop. Recurring events route through the recurring
  //    edit prompt; one-time series can move directly.
  function handleDragStart(e, occ) {
    setDraggedCall(occ);
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleDragOver(e, dateStr) {
    if (!draggedCall || dateStr === draggedCall.date) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(dateStr);
  }
  function handleDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOverDate(null);
  }
  function handleDrop(e, toDate) {
    e.preventDefault();
    setDragOverDate(null);
    if (!draggedCall || toDate === draggedCall.date) {
      setDraggedCall(null);
      return;
    }
    setMoveModal({ occurrence: draggedCall, toDate, time: draggedCall.time });
    setDraggedCall(null);
  }
  function handleDragEnd() {
    setDraggedCall(null);
    setDragOverDate(null);
  }

  // ── Mutations
  async function createSeries(payload) {
    const { error } = await supabase
      .from('coaching_call_schedules')
      .insert({ coach_id: profile.id, active: true, ...payload });
    if (error) throw error;
    await loadSeries();
  }

  async function updateSeries(id, patch) {
    const { error } = await supabase
      .from('coaching_call_schedules')
      .update(patch)
      .eq('id', id);
    if (error) throw error;
    await loadSeries();
  }

  async function deactivateSeries(id) {
    await supabase
      .from('coaching_call_schedules')
      .update({ active: false })
      .eq('id', id);
    await loadSeries();
  }

  async function upsertOverride(occurrence, patch) {
    // PostgREST's .upsert(..., { onConflict }) requires a real
    // unique constraint on the named columns; coaching_call_overrides
    // doesn't have one, so a previous attempt to use it broke the
    // status picker silently. Query first, then update or insert —
    // works regardless of schema-level constraints.
    let existingId = occurrence.overrideId;
    if (!existingId) {
      const { data: existing } = await supabase
        .from('coaching_call_overrides')
        .select('id')
        .eq('schedule_id', occurrence.seriesId)
        .eq('original_date', occurrence.originalDate ?? occurrence.date)
        .maybeSingle();
      existingId = existing?.id;
    }

    let row;
    if (existingId) {
      const { data, error } = await supabase
        .from('coaching_call_overrides')
        .update(patch)
        .eq('id', existingId)
        .select()
        .single();
      if (error) throw error;
      row = data;
    } else {
      const { data, error } = await supabase
        .from('coaching_call_overrides')
        .insert({
          coach_id: profile.id,
          schedule_id: occurrence.seriesId,
          original_date: occurrence.originalDate ?? occurrence.date,
          ...patch,
        })
        .select()
        .single();
      if (error) throw error;
      row = data;
    }
    await loadOverrides();
    return row;
  }

  // "This and future" split: cap the existing series the day before
  // the split, and (for edits) insert a fresh series starting on the
  // split date with the new settings. For deletes, just cap.
  async function splitSeriesAt(seriesRow, splitDateStr, newSettings = null) {
    const splitDate = parseDateStr(splitDateStr);
    const cap = new Date(splitDate.getTime() - 86400000);
    await supabase
      .from('coaching_call_schedules')
      .update({ series_ends_at: toDateStr(cap) })
      .eq('id', seriesRow.id);

    if (newSettings) {
      const refDate = newSettings.frequency === 'one-time'
        ? splitDateStr
        : (newSettings.biweekly_ref_date ?? splitDateStr);
      await supabase
        .from('coaching_call_schedules')
        .insert({
          coach_id: profile.id,
          active: true,
          athlete_id: newSettings.athlete_id,
          day_of_week: newSettings.day_of_week,
          call_time: newSettings.call_time,
          timezone: newSettings.timezone,
          frequency: newSettings.frequency,
          biweekly_ref_date: refDate,
          duration_minutes: newSettings.duration_minutes,
          description: newSettings.description,
        });
    }
    await loadSeries();
    await loadOverrides();
  }

  async function saveNote(occurrence, text) {
    const key = `${occurrence.athleteId}:${occurrence.date}`;
    const existing = notesByKey[key];
    if (existing) {
      await supabase
        .from('coaching_call_notes')
        .update({ notes: text, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
    } else {
      await supabase.from('coaching_call_notes').insert({
        coach_id: profile.id,
        athlete_id: occurrence.athleteId,
        schedule_id: occurrence.seriesId,
        call_date: occurrence.date,
        notes: text,
      });
    }
    await loadMonthNotes();
  }

  async function deleteNote(occurrence) {
    const key = `${occurrence.athleteId}:${occurrence.date}`;
    const existing = notesByKey[key];
    if (!existing) return;
    await supabase
      .from('coaching_call_notes')
      .delete()
      .eq('id', existing.id);
    await loadMonthNotes();
  }

  // ── Render
  return (
    <Layout>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p className="page-eyebrow">Coach</p>
          <h1 className="page-title">Coaching Calls</h1>
        </div>
        {athletes.length > 0 && (
          <button
            className="btn btn-primary"
            style={{ width: 'auto', padding: '10px 20px' }}
            onClick={() => setCreateModal({ date: todayStr })}
          >
            + New Call
          </button>
        )}
      </div>

      {/* ── Filter bar */}
      <FilterBar
        athletes={athletes}
        filterAthlete={filterAthlete}
        setFilterAthlete={setFilterAthlete}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      {/* ── Calendar (primary surface) */}
      <div className="section-card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', borderBottom: '1px solid var(--gray-100)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn-xs btn-xs-ghost" onClick={goPrev}>‹</button>
            <button className="btn-xs btn-xs-ghost" onClick={goNext}>›</button>
            <button className="btn-xs btn-xs-ghost" onClick={goToday}>Today</button>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginLeft: 8 }}>
              {MONTHS[calMonth]} {calYear}
            </span>
          </div>
          <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
            Click any date to schedule · Click a call to open
          </span>
        </div>

        <CalendarGrid
          weeks={weeks}
          occByDate={occByDate}
          notesByKey={notesByKey}
          todayStr={todayStr}
          getAthleteName={getAthleteName}
          dragOverDate={dragOverDate}
          onCellClick={dateStr => setCreateModal({ date: dateStr })}
          onCallClick={occ => setDetailModal(occ)}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
        />
      </div>

      {/* ── Active Schedules secondary accordion */}
      <ActiveSchedulesAccordion
        open={showActiveSchedules}
        onToggle={() => setShowActiveSchedules(v => !v)}
        series={series}
        getAthleteName={getAthleteName}
        loading={loading}
      />

      {/* ── Create call */}
      {createModal && (
        <CreateCallModal
          initialDate={createModal.date}
          athletes={athletes}
          onClose={() => setCreateModal(null)}
          onCreate={async payload => {
            await createSeries(payload);
            setCreateModal(null);
          }}
        />
      )}

      {/* ── Call detail */}
      {detailModal && (
        <CallDetailModal
          occurrence={detailModal}
          athleteName={getAthleteName(detailModal.athleteId)}
          existingNote={notesByKey[`${detailModal.athleteId}:${detailModal.date}`] ?? null}
          todayStr={todayStr}
          onClose={() => setDetailModal(null)}
          onEditRequest={() => setEditChoice({ occurrence: detailModal, mode: 'edit' })}
          onDeleteRequest={() => setEditChoice({ occurrence: detailModal, mode: 'delete' })}
          onSaveNote={async text => {
            await saveNote(detailModal, text);
          }}
          onDeleteNote={async () => {
            await deleteNote(detailModal);
          }}
          onSetStatus={async status => {
            try {
              const row = await upsertOverride(detailModal, {
                status: status || null,
              });
              // Sync the modal with the fresh override row so a
              // subsequent click on a different status updates the
              // same row instead of attempting another insert.
              setDetailModal(prev => prev ? {
                ...prev,
                status: status || null,
                overrideId: row?.id ?? prev.overrideId,
                override: row ?? prev.override,
              } : prev);
            } catch (e) {
              // Surface the failure instead of silently snapping
              // back — users were stuck with no signal that the
              // database call had been rejected.
              console.error('[coaching-calls] status update failed:', e);
              window.alert(`Could not update status: ${e?.message ?? e}`);
            }
          }}
        />
      )}

      {/* ── Recurring this/future/series prompt */}
      {editChoice && (
        <RecurringActionPrompt
          mode={editChoice.mode}
          occurrence={editChoice.occurrence}
          athletes={athletes}
          onClose={() => setEditChoice(null)}
          onPickEditScope={async (scope, edits) => {
            const occ = editChoice.occurrence;
            if (scope === 'one') {
              await upsertOverride(occ, {
                new_date: edits.date,
                new_time: edits.time,
                description: edits.description ?? null,
                duration_minutes: edits.duration ?? null,
                cancelled: false,
              });
            } else if (scope === 'future') {
              await splitSeriesAt(occ.series, occ.date, {
                athlete_id: occ.series.athlete_id,
                day_of_week: parseDateStr(edits.date).getDay(),
                call_time: edits.time,
                timezone: occ.series.timezone,
                frequency: edits.frequency,
                biweekly_ref_date: edits.date,
                duration_minutes: edits.duration,
                description: edits.description ?? null,
              });
            } else if (scope === 'series') {
              await updateSeries(occ.seriesId, {
                day_of_week: parseDateStr(edits.date).getDay(),
                call_time: edits.time,
                timezone: edits.timezone,
                frequency: edits.frequency,
                biweekly_ref_date: edits.date,
                duration_minutes: edits.duration,
                description: edits.description ?? null,
              });
            }
            setEditChoice(null);
            setDetailModal(null);
          }}
          onPickDeleteScope={async scope => {
            const occ = editChoice.occurrence;
            if (scope === 'one') {
              await upsertOverride(occ, { cancelled: true, status: 'cancelled' });
            } else if (scope === 'future') {
              await splitSeriesAt(occ.series, occ.date, null);
            } else if (scope === 'series') {
              await deactivateSeries(occ.seriesId);
            }
            setEditChoice(null);
            setDetailModal(null);
          }}
        />
      )}

      {/* ── Drag-drop move confirm (kept simple) */}
      {moveModal && (
        <MoveCallModal
          modal={moveModal}
          onClose={() => setMoveModal(null)}
          onConfirm={async () => {
            const { occurrence, toDate, time } = moveModal;
            await upsertOverride(occurrence, {
              new_date: toDate,
              new_time: time || occurrence.time,
              cancelled: false,
            });
            setMoveModal(null);
          }}
          setTime={time => setMoveModal(m => m ? { ...m, time } : m)}
        />
      )}
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────
// Filter bar
// ─────────────────────────────────────────────────────────────────

function FilterBar({ athletes, filterAthlete, setFilterAthlete, filterStatus, setFilterStatus }) {
  const STATUS_FILTERS = [
    { value: 'all',       label: 'All' },
    { value: 'upcoming',  label: 'Upcoming' },
    { value: 'completed', label: 'Completed' },
    { value: 'missed',    label: 'Missed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
  return (
    <div
      className="section-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '12px 16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
          Athlete
        </span>
        <select
          className="form-input form-select"
          value={filterAthlete}
          onChange={e => setFilterAthlete(e.target.value)}
          style={{ padding: '6px 10px', fontSize: 13, minWidth: 160 }}
        >
          <option value="all">All athletes</option>
          {athletes.map(a => (
            <option key={a.id} value={a.id}>{a.full_name}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: 'var(--gray-500)', textTransform: 'uppercase', marginRight: 4 }}>
          Status
        </span>
        {STATUS_FILTERS.map(s => {
          const selected = filterStatus === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setFilterStatus(s.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: '1.5px solid',
                borderColor: selected ? 'var(--navy)' : 'var(--gray-200)',
                background: selected ? 'var(--navy)' : '#fff',
                color: selected ? '#fff' : 'var(--gray-700)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Calendar grid
// ─────────────────────────────────────────────────────────────────

function CalendarGrid({
  weeks, occByDate, notesByKey, todayStr, getAthleteName,
  dragOverDate, onCellClick, onCallClick,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}) {
  return (
    <div style={{ padding: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DOW_LABELS.map(d => (
          <div key={d} style={{ padding: '6px 4px', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textAlign: 'center', letterSpacing: 0.6 }}>
            {d.slice(0, 3)}
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
          {week.map((dateStr, di) => {
            if (!dateStr) {
              return <div key={di} style={{ minHeight: 96, background: '#fafbfc' }} />;
            }
            const isToday = dateStr === todayStr;
            const isDragOver = dragOverDate === dateStr;
            const occs = occByDate[dateStr] ?? [];
            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => onCellClick(dateStr)}
                onDragOver={e => onDragOver(e, dateStr)}
                onDragLeave={onDragLeave}
                onDrop={e => onDrop(e, dateStr)}
                style={{
                  position: 'relative',
                  minHeight: 100,
                  textAlign: 'left',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  padding: 6,
                  background: isDragOver
                    ? 'rgba(252,198,37,0.15)'
                    : isToday
                    ? 'rgba(0,24,44,0.04)'
                    : '#fff',
                  outline: isDragOver ? '2px dashed var(--gold)' : 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: isToday ? 800 : 600,
                    color: isToday ? 'var(--navy)' : 'var(--gray-500)',
                  }}
                >
                  {parseDateStr(dateStr).getDate()}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflow: 'hidden' }}>
                  {occs.slice(0, 3).map((o, i) => {
                    const eff = effectiveStatus(o, todayStr);
                    const palette = statusColor(eff);
                    const noteKey = `${o.athleteId}:${o.date}`;
                    const hasNote = !!notesByKey[noteKey]?.notes?.trim();
                    return (
                      <div
                        key={`${o.seriesId}-${o.date}-${i}`}
                        draggable={true}
                        onDragStart={e => { e.stopPropagation(); onDragStart(e, o); }}
                        onDragEnd={onDragEnd}
                        onClick={e => { e.stopPropagation(); onCallClick(o); }}
                        style={{
                          background: palette.bg,
                          color: palette.fg,
                          border: `1px solid ${palette.border}`,
                          borderRadius: 4,
                          padding: '3px 6px',
                          fontSize: 11,
                          fontWeight: 600,
                          textAlign: 'left',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textDecoration: o.cancelled ? 'line-through' : 'none',
                        }}
                        title={`${getAthleteName(o.athleteId)} · ${fmt12(o.time)}`}
                      >
                        {fmt12(o.time)} {getAthleteName(o.athleteId).split(' ')[0]}
                        {hasNote && <span style={{ marginLeft: 4 }}>📝</span>}
                      </div>
                    );
                  })}
                  {occs.length > 3 && (
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 600 }}>
                      +{occs.length - 3} more
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Active Schedules accordion (secondary surface)
// ─────────────────────────────────────────────────────────────────

function ActiveSchedulesAccordion({ open, onToggle, series, getAthleteName, loading }) {
  return (
    <div className="section-card" style={{ marginTop: 16 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '12px 20px',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
          Active Schedules ({series.length})
        </span>
        <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>
          {open ? '▲ Hide' : '▼ Show'}
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px' }}>
          {loading ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-400)' }}>Loading…</div>
          ) : series.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-400)', fontStyle: 'italic' }}>
              No active schedules. Click a calendar date above to add one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {series.map(s => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    borderRadius: 6,
                    background: 'var(--gray-50, #f9fafb)',
                    border: '1px solid var(--gray-100)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                      {getAthleteName(s.athlete_id)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                      {describeRecurrence(s)}
                      {s.duration_minutes ? ` · ${s.duration_minutes} min` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create call modal
// ─────────────────────────────────────────────────────────────────

function CreateCallModal({ initialDate, athletes, onClose, onCreate }) {
  const [athleteId, setAthleteId] = useState(athletes[0]?.id ?? '');
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState('19:30');
  const [duration, setDuration] = useState(30);
  const [tz, setTz] = useState('America/Denver');
  const [frequency, setFrequency] = useState('one-time');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    if (!athleteId) { setError('Pick an athlete.'); return; }
    if (!date) { setError('Pick a date.'); return; }
    setError('');
    setSaving(true);
    try {
      const dow = parseDateStr(date).getDay();
      await onCreate({
        athlete_id: athleteId,
        day_of_week: dow,
        call_time: time,
        timezone: tz,
        frequency,
        biweekly_ref_date: date, // serves as series start for any recurrence
        duration_minutes: Number(duration),
        description: description.trim() || null,
      });
    } catch (err) {
      setError(err.message ?? 'Failed to create.');
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Schedule a Call" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {athletes.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            No athletes on your roster yet.
          </p>
        ) : (
          <FormRow label="Athlete">
            <select
              className="form-input form-select"
              value={athleteId}
              onChange={e => setAthleteId(e.target.value)}
            >
              {athletes.map(a => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          </FormRow>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormRow label="Date">
            <input
              type="date"
              className="form-input"
              value={date}
              onChange={e => setDate(e.target.value)}
              required
            />
          </FormRow>
          <FormRow label="Start time">
            <input
              type="time"
              className="form-input"
              value={time}
              onChange={e => setTime(e.target.value)}
            />
          </FormRow>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormRow label="Duration">
            <select
              className="form-input form-select"
              value={duration}
              onChange={e => setDuration(Number(e.target.value))}
            >
              {DURATION_OPTIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </FormRow>
          <FormRow label="Timezone">
            <select
              className="form-input form-select"
              value={tz}
              onChange={e => setTz(e.target.value)}
            >
              {TIMEZONES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </FormRow>
        </div>

        <FormRow label="Recurrence">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FREQUENCY_OPTIONS.map(f => {
              const selected = frequency === f.value;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrequency(f.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: '1.5px solid',
                    borderColor: selected ? 'var(--navy)' : 'var(--gray-200)',
                    background: selected ? 'var(--navy)' : '#fff',
                    color: selected ? '#fff' : 'var(--gray-700)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </FormRow>

        <FormRow label="Description / agenda (optional)">
          <textarea
            className="form-input"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Goals, items to cover, prep…"
          />
        </FormRow>

        {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn-xs btn-xs-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || athletes.length === 0}
            style={{ width: 'auto', padding: '10px 20px' }}
          >
            {saving ? 'Saving…' : 'Schedule call'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Call detail modal — open when clicking an event pill
// ─────────────────────────────────────────────────────────────────

function CallDetailModal({
  occurrence, athleteName, existingNote, todayStr,
  onClose, onEditRequest, onDeleteRequest,
  onSaveNote, onDeleteNote, onSetStatus,
}) {
  const eff = effectiveStatus(occurrence, todayStr);
  const palette = statusColor(eff);
  const dateLabel = parseDateStr(occurrence.date).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const recurrenceLabel = describeRecurrence(occurrence.series);
  // Status applies to this single occurrence, not the series.
  // Keep the badge on its own line with an explicit "this call"
  // qualifier so a Completed mark doesn't read as if the whole
  // weekly series were closed out.
  return (
    <ModalShell
      title={athleteName}
      subtitle={`${dateLabel} · ${fmt12(occurrence.time)}${occurrence.duration ? ` · ${occurrence.duration} min` : ''} · ${recurrenceLabel}`}
      onClose={onClose}
      wide
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', letterSpacing: 1, textTransform: 'uppercase' }}>
            This call
          </span>
          <span
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              background: palette.bg,
              color: palette.fg,
              border: `1px solid ${palette.border}`,
            }}
          >
            {statusLabel(eff)}
          </span>
        </div>

        {occurrence.description && (
          <div
            style={{
              background: 'var(--gray-50, #f9fafb)',
              border: '1px solid var(--gray-100)',
              borderRadius: 6,
              padding: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 4 }}>
              Agenda
            </div>
            <div style={{ fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>
              {occurrence.description}
            </div>
          </div>
        )}

        <StatusPicker current={occurrence.status ?? ''} onPick={onSetStatus} />

        <NoteEditor
          existing={existingNote}
          onSave={onSaveNote}
          onDelete={onDeleteNote}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 12,
            borderTop: '1px solid var(--gray-100)',
          }}
        >
          <button
            className="btn-xs btn-xs-ghost"
            onClick={onDeleteRequest}
            style={{ color: '#dc2626', fontWeight: 600 }}
          >
            Delete event
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-xs btn-xs-ghost" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-primary" onClick={onEditRequest} style={{ width: 'auto', padding: '8px 16px' }}>
              Edit event
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function StatusPicker({ current, onPick }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 6 }}>
        Mark this call
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUS_OPTIONS.map(s => {
          const selected = (current ?? '') === s.value;
          return (
            <button
              key={s.value || 'upcoming'}
              type="button"
              onClick={() => onPick(s.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border: '1.5px solid',
                borderColor: selected ? 'var(--navy)' : 'var(--gray-200)',
                background: selected ? 'var(--navy)' : '#fff',
                color: selected ? '#fff' : 'var(--gray-700)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Recurring action prompt — Edit / Delete with three scopes
// ─────────────────────────────────────────────────────────────────

function RecurringActionPrompt({
  mode, occurrence, athletes, onClose, onPickEditScope, onPickDeleteScope,
}) {
  const isRecurring = occurrence.series?.frequency && occurrence.series.frequency !== 'one-time';
  const [scope, setScope] = useState(isRecurring ? null : 'series');

  // Edit form fields when scope picked. Pre-fill from occurrence.
  const [date, setDate] = useState(occurrence.date);
  const [time, setTime] = useState(occurrence.time ?? '19:30');
  const [duration, setDuration] = useState(occurrence.duration ?? 30);
  const [frequency, setFrequency] = useState(occurrence.series?.frequency ?? 'one-time');
  const [description, setDescription] = useState(occurrence.description ?? '');
  const [timezone, setTimezone] = useState(occurrence.series?.timezone ?? 'America/Denver');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (mode === 'delete') {
    return (
      <ModalShell title="Delete call" subtitle="What should we delete?" onClose={onClose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ScopeButton
            label="Just this call"
            description={`Cancels ${parseDateStr(occurrence.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} only. The rest of the series stays.`}
            onClick={() => onPickDeleteScope('one')}
          />
          {isRecurring && (
            <>
              <ScopeButton
                label="This and future calls"
                description="Stops the series from this date forward. Past calls and notes stay untouched."
                onClick={() => onPickDeleteScope('future')}
              />
              <ScopeButton
                label="Entire series"
                description="Deactivates the recurring schedule. Historical notes stay in place."
                onClick={() => onPickDeleteScope('series')}
                danger
              />
            </>
          )}
          <button className="btn-xs btn-xs-ghost" onClick={onClose} style={{ marginTop: 8 }}>
            Cancel
          </button>
        </div>
      </ModalShell>
    );
  }

  // Edit mode — first prompt for scope (if recurring), then show form.
  if (isRecurring && !scope) {
    return (
      <ModalShell title="Edit call" subtitle="What should we update?" onClose={onClose}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ScopeButton
            label="Just this call"
            description={`Edit ${parseDateStr(occurrence.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} only.`}
            onClick={() => setScope('one')}
          />
          <ScopeButton
            label="This and future calls"
            description="Splits the series. Past calls keep their original time + notes."
            onClick={() => setScope('future')}
          />
          <ScopeButton
            label="Entire series"
            description="Updates the recurring schedule going forward. Historical occurrences with overrides stay."
            onClick={() => setScope('series')}
          />
          <button className="btn-xs btn-xs-ghost" onClick={onClose} style={{ marginTop: 8 }}>
            Cancel
          </button>
        </div>
      </ModalShell>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onPickEditScope(scope, {
        date,
        time,
        duration: Number(duration),
        frequency,
        description: description.trim() || null,
        timezone,
      });
    } catch (err) {
      setError(err.message ?? 'Failed to save.');
      setSaving(false);
    }
  }

  const scopeLabel = scope === 'one'
    ? 'Just this call'
    : scope === 'future'
    ? 'This and future calls'
    : 'Entire series';

  return (
    <ModalShell
      title="Edit call"
      subtitle={scopeLabel}
      onClose={onClose}
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormRow label="Date">
            <input
              type="date"
              className="form-input"
              value={date}
              onChange={e => setDate(e.target.value)}
              required
              disabled={scope === 'series'}
            />
          </FormRow>
          <FormRow label="Start time">
            <input
              type="time"
              className="form-input"
              value={time}
              onChange={e => setTime(e.target.value)}
            />
          </FormRow>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormRow label="Duration">
            <select
              className="form-input form-select"
              value={duration}
              onChange={e => setDuration(Number(e.target.value))}
            >
              {DURATION_OPTIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </FormRow>
          {scope !== 'one' && (
            <FormRow label="Timezone">
              <select
                className="form-input form-select"
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
              >
                {TIMEZONES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </FormRow>
          )}
        </div>

        {scope !== 'one' && (
          <FormRow label="Recurrence">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FREQUENCY_OPTIONS.map(f => {
                const selected = frequency === f.value;
                return (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFrequency(f.value)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 999,
                      border: '1.5px solid',
                      borderColor: selected ? 'var(--navy)' : 'var(--gray-200)',
                      background: selected ? 'var(--navy)' : '#fff',
                      color: selected ? '#fff' : 'var(--gray-700)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </FormRow>
        )}

        <FormRow label="Description / agenda (optional)">
          <textarea
            className="form-input"
            rows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </FormRow>

        {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          {isRecurring ? (
            <button type="button" className="btn-xs btn-xs-ghost" onClick={() => setScope(null)}>
              ← Change scope
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-xs btn-xs-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: 'auto', padding: '10px 20px' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

function ScopeButton({ label, description, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '12px 14px',
        borderRadius: 8,
        border: '1.5px solid var(--gray-200)',
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 700, color: danger ? '#dc2626' : 'var(--navy)' }}>
        {label}
      </span>
      <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{description}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Note editor — markdown source-edit + toolbar + template insert
// ─────────────────────────────────────────────────────────────────

function NoteEditor({ existing, onSave, onDelete }) {
  const [editing, setEditing] = useState(!existing?.notes?.trim());
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [empty, setEmpty] = useState(!existing?.notes?.trim());
  const editorRef = useRef(null);

  // Seed the editor's HTML once when the modal opens or the
  // underlying note swaps. We deliberately skip syncing innerHTML on
  // every keystroke — that would fight the cursor and clobber typing.
  useEffect(() => {
    setEditing(!existing?.notes?.trim());
    setSavedAt(null);
    setEmpty(!existing?.notes?.trim());
    if (editorRef.current) {
      editorRef.current.innerHTML = noteToHtml(existing?.notes ?? '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.notes]);

  // Re-seed when entering edit mode after a save, since the editor
  // div unmounts in preview mode.
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.innerHTML = noteToHtml(existing?.notes ?? '');
      setEmpty(!editorRef.current.innerText.trim());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  function exec(cmd, value = null) {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    setEmpty(!editorRef.current?.innerText.trim());
    setSavedAt(null);
  }

  function applyFormat(kind) {
    if (kind === 'bold')      return exec('bold');
    if (kind === 'italic')    return exec('italic');
    if (kind === 'underline') return exec('underline');
    if (kind === 'h2')        return exec('formatBlock', '<h3>');
    if (kind === 'ul')        return exec('insertUnorderedList');
    if (kind === 'ol')        return exec('insertOrderedList');
    if (kind === 'link') {
      const url = window.prompt('Link URL', 'https://');
      if (url) exec('createLink', url);
      return;
    }
    if (kind === 'template') {
      editorRef.current?.focus();
      // If the editor is empty, replace it; otherwise append.
      if (!editorRef.current?.innerText.trim()) {
        editorRef.current.innerHTML = NOTE_TEMPLATE_HTML;
      } else {
        document.execCommand('insertHTML', false, NOTE_TEMPLATE_HTML);
      }
      setEmpty(false);
      setSavedAt(null);
    }
  }

  function handleInput() {
    setSavedAt(null);
    setEmpty(!editorRef.current?.innerText.trim());
  }

  async function save() {
    setSaving(true);
    try {
      const html = editorRef.current?.innerHTML ?? '';
      // Treat a contentEditable that only has whitespace / empty
      // tags as truly empty so we don't persist `<p><br></p>`.
      const text = editorRef.current?.innerText.trim() ?? '';
      await onSave(text ? html : '');
      setSavedAt(new Date());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete these notes? This cannot be undone.')) return;
    await onDelete();
    if (editorRef.current) editorRef.current.innerHTML = '';
    setEmpty(true);
    setEditing(true);
    setSavedAt(null);
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: 'var(--gray-500)', textTransform: 'uppercase' }}>
          Call notes
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {savedAt && !editing && (
            <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
              ✓ Saved
            </span>
          )}
          {!editing && existing?.notes?.trim() && (
            <button className="btn-xs btn-xs-ghost" onClick={() => setEditing(true)}>
              Edit
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div
          style={{
            border: '1.5px solid var(--gray-200)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              padding: 6,
              background: 'var(--gray-50, #f9fafb)',
              borderBottom: '1px solid var(--gray-200)',
            }}
          >
            <ToolbarBtn label="B"  bold onClick={() => applyFormat('bold')}      title="Bold" />
            <ToolbarBtn label="I"  italic onClick={() => applyFormat('italic')}  title="Italic" />
            <ToolbarBtn label="U"  underline onClick={() => applyFormat('underline')} title="Underline" />
            <ToolbarBtn label="H"  onClick={() => applyFormat('h2')}             title="Heading" />
            <ToolbarBtn label="•"  onClick={() => applyFormat('ul')}             title="Bulleted list" />
            <ToolbarBtn label="1."  onClick={() => applyFormat('ol')}            title="Numbered list" />
            <ToolbarBtn label="🔗" onClick={() => applyFormat('link')}           title="Link" />
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => applyFormat('template')}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                border: '1px solid var(--gray-200)',
                background: '#fff',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--gray-700)',
                cursor: 'pointer',
              }}
            >
              + Insert template
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            {empty && (
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--gray-400)',
                  pointerEvents: 'none',
                  fontStyle: 'italic',
                }}
              >
                Agenda, athlete updates, action items… use the toolbar to format.
              </div>
            )}
            <div
              ref={editorRef}
              className="note-preview"
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onBlur={handleInput}
              style={{
                minHeight: 180,
                maxHeight: 360,
                overflowY: 'auto',
                padding: 12,
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                lineHeight: 1.5,
                color: 'var(--gray-700)',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 10px',
              background: 'var(--gray-50, #f9fafb)',
              borderTop: '1px solid var(--gray-200)',
              gap: 6,
            }}
          >
            <div>
              {existing?.notes?.trim() && (
                <button
                  type="button"
                  className="btn-xs btn-xs-ghost"
                  onClick={handleDelete}
                  style={{ color: '#dc2626', fontWeight: 600 }}
                >
                  Delete notes
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {existing?.notes?.trim() && (
                <button
                  type="button"
                  className="btn-xs btn-xs-ghost"
                  onClick={() => {
                    if (editorRef.current) editorRef.current.innerHTML = noteToHtml(existing.notes);
                    setSavedAt(null);
                    setEditing(false);
                  }}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={save}
                disabled={saving}
                style={{ width: 'auto', padding: '6px 14px', fontSize: 13 }}
              >
                {saving ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <NotePreview html={noteToHtml(existing?.notes ?? '')} />
      )}
    </div>
  );
}

function ToolbarBtn({ label, onClick, title, bold, italic, underline }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 32,
        height: 30,
        borderRadius: 6,
        border: '1px solid var(--gray-200)',
        background: '#fff',
        fontSize: 13,
        fontWeight: bold ? 700 : 600,
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: underline ? 'underline' : 'none',
        color: 'var(--gray-700)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function NotePreview({ html }) {
  return (
    <div
      className="note-preview"
      style={{
        border: '1px solid var(--gray-100)',
        borderRadius: 8,
        padding: '14px 18px',
        background: '#fff',
        minHeight: 80,
        fontSize: 13,
        lineHeight: 1.55,
        color: 'var(--gray-700)',
      }}
      dangerouslySetInnerHTML={{ __html: html || '<p style="color: var(--gray-400); font-style: italic; margin: 0;">No notes yet — add one to remember what was covered.</p>' }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Move-call confirm (drag-and-drop landing)
// ─────────────────────────────────────────────────────────────────

function MoveCallModal({ modal, onClose, onConfirm, setTime }) {
  const [saving, setSaving] = useState(false);
  return (
    <ModalShell
      title="Move call"
      subtitle={`From ${modal.occurrence.date} to ${modal.toDate}`}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0 }}>
          This moves only this single occurrence. The recurring series stays unchanged.
        </p>
        <FormRow label="Time at new date">
          <input
            type="time"
            className="form-input"
            value={modal.time ?? ''}
            onChange={e => setTime(e.target.value)}
          />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-xs btn-xs-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={async () => { setSaving(true); await onConfirm(); }}
            disabled={saving}
            style={{ width: 'auto', padding: '8px 16px' }}
          >
            {saving ? 'Moving…' : 'Move call'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// Modal shell + form row
// ─────────────────────────────────────────────────────────────────

function ModalShell({ title, subtitle, onClose, children, wide }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 24, 44, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: 20,
          width: '100%',
          maxWidth: wide ? 640 : 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '4px 0 0' }}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--gray-400)', cursor: 'pointer', padding: 0, lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="form-group" style={{ marginBottom: 0 }}>
      <label className="form-label" style={{ marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
